import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../api/api';
import { 
    FaBell, FaCheckDouble, FaBriefcase, FaMoneyBillWave, 
    FaInfoCircle, FaClock, FaCheckCircle, FaTrashAlt, FaArrowLeft
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';

const NotificationsPage = () => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const { user } = useAuth();
    const navigate = useNavigate();

    const fetchNotifications = useCallback(async () => {
        setLoading(true);
        try {
            const response = await apiClient.get('/notifications');
            setNotifications(response.data);
        } catch (error) {
            console.error("Failed to fetch notifications:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchNotifications();
    }, [fetchNotifications]);

    const handleMarkAsRead = async (id) => {
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, status: 'read' } : n));
        try {
            await apiClient.post(`/notifications/${id}/read`);
        } catch (error) {
            console.error("Failed to mark notification as read:", error);
        }
    };

    const handleMarkAllRead = async () => {
        if (!window.confirm("Mark all notifications as read?")) return;
        const unreadIds = notifications.filter(n => n.status === 'unread').map(n => n.id);
        if (unreadIds.length === 0) return;

        setNotifications(prev => prev.map(n => ({ ...n, status: 'read' })));
        try {
            await Promise.all(unreadIds.map(id => apiClient.post(`/notifications/${id}/read`)));
        } catch (err) {
            console.error("Failed to mark all as read");
        }
    };

    const handleNotificationClick = (n) => {
        handleMarkAsRead(n.id);
        
        if (n.link) {
            navigate(n.link);
            return;
        }

        const type = n.type?.toUpperCase();
        const id = n.relatedEntityId;

        // --- STRICT ROLE-BASED REDIRECTION ---
        if (user?.role === 'worker') {
            if (['JOB_OFFER', 'DIRECT_HIRE', 'BID_ACCEPTED', 'JOB_STARTED', 'HIRE_STATUS_UPDATE', 'WORKER_ARRIVED', 'ROSTER_INVITE'].includes(type)) {
                navigate('/worker-dashboard/running-jobs');
                return;
            }
        }

        switch (type) {
            case 'JOB_COMPLETED':
            case 'WALLET_RECHARGE':
            case 'WITHDRAWAL_REQUEST':
                navigate('/wallet');
                break;

            case 'JOB_APPLICATION':
            case 'NEW_JOB_OPPORTUNITY':
                if (id) navigate(`/job/${id}`);
                break;
            
            case 'JOB_START_REQUEST':
            case 'JOB_COMPLETION_REQUEST':
            case 'WORKER_ARRIVING':
            case 'WORKER_ARRIVED':
            case 'HIRE_STATUS_UPDATE':
                if (id) navigate(`/manage-job/${id}`);
                break;

            default:
                if (user?.role === 'buyer') navigate('/owner-dashboard');
                else navigate('/worker-dashboard');
                break;
        }
    };

    const getIcon = (type) => {
        const t = type?.toUpperCase();
        if (['JOB_APPLICATION', 'JOB_OFFER', 'BID_ACCEPTED', 'DIRECT_HIRE'].includes(t)) {
            return <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center"><FaBriefcase size={20} /></div>;
        }
        if (['WALLET_RECHARGE', 'WITHDRAWAL_REQUEST', 'JOB_COMPLETED'].includes(t)) {
            return <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center"><FaMoneyBillWave size={20} /></div>;
        }
        return <div className="w-12 h-12 rounded-2xl bg-slate-50 text-slate-500 flex items-center justify-center"><FaInfoCircle size={20} /></div>;
    };

    return (
        <div className="min-h-screen bg-slate-50 font-sans pb-20">
            <div className="bg-white border-b border-slate-200 sticky top-0 z-30">
                <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button onClick={() => navigate(-1)} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
                            <FaArrowLeft className="text-slate-400" />
                        </button>
                        <h1 className="text-2xl font-black text-slate-800 tracking-tight">Notification History</h1>
                    </div>
                    <div className="flex gap-2">
                        <button 
                            onClick={handleMarkAllRead}
                            className="p-3 bg-emerald-50 text-emerald-600 rounded-xl hover:bg-emerald-600 hover:text-white transition-all shadow-sm flex items-center gap-2 text-xs font-black uppercase tracking-wider"
                        >
                            <FaCheckDouble /> Mark All Read
                        </button>
                    </div>
                </div>
            </div>

            <main className="max-w-4xl mx-auto px-6 py-10">
                {loading ? (
                    <div className="space-y-4">
                        {[1,2,3,4].map(i => <div key={i} className="h-24 bg-white rounded-3xl animate-pulse"></div>)}
                    </div>
                ) : notifications.length > 0 ? (
                    <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-200 overflow-hidden divide-y divide-slate-50">
                        {notifications.map(n => (
                            <div 
                                key={n.id} 
                                onClick={() => handleNotificationClick(n)}
                                className={`p-8 hover:bg-slate-50 transition-all cursor-pointer flex gap-6 items-start relative ${n.status === 'unread' ? 'bg-emerald-50/20' : ''}`}
                            >
                                {n.status === 'unread' && (
                                    <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-emerald-500"></div>
                                )}
                                <div className="flex-shrink-0">
                                    {getIcon(n.type)}
                                </div>
                                <div className="flex-1">
                                    <div className="flex justify-between items-start mb-2">
                                        <p className={`text-lg leading-tight ${n.status === 'unread' ? 'text-slate-900 font-black' : 'text-slate-600 font-bold'}`}>
                                            {n.message}
                                        </p>
                                        <span className="text-xs text-slate-400 font-bold whitespace-nowrap ml-4">
                                            {new Date(n.createdAt).toLocaleDateString()}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <span className="px-2.5 py-0.5 rounded-lg bg-slate-100 text-slate-500 text-[10px] font-black uppercase tracking-widest border border-slate-200">
                                            {n.type?.replace('_', ' ')}
                                        </span>
                                        {n.status === 'unread' && (
                                            <span className="text-[10px] font-black text-emerald-600 uppercase tracking-widest flex items-center gap-1">
                                                <FaCheckCircle /> New
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="bg-white rounded-[3rem] p-20 text-center border-2 border-dashed border-slate-200">
                        <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6 text-slate-200">
                            <FaBell size={32} />
                        </div>
                        <h3 className="text-xl font-black text-slate-800 uppercase tracking-widest">Inbox Empty</h3>
                        <p className="text-slate-400 mt-2 font-medium">Your notification history will appear here once you start using the platform.</p>
                        <button onClick={() => navigate('/')} className="mt-8 bg-slate-900 text-white px-8 py-3 rounded-xl font-bold hover:bg-slate-800 transition-all shadow-lg">Return to Home</button>
                    </div>
                )}
            </main>
        </div>
    );
};

export default NotificationsPage;
